import { cva } from 'class-variance-authority';

/**
 * @description Variants for typography. You need to use the typographyVariants function to get the class names for the typography styles.
 * @param {string} variant - The variant of the typography: h1, h2, h3, h4, h5, h6 - should be used with the same html tag as the variant and bodyShort01, bodyShort02, bodyLong01, bodyLong02, bodyMobile01, bodyMobile02, bodyMobile03, caption1, caption2, caption3, label01, overline1, helperText01 - should be used with appropriate html tag as p/span/div etc.
 * @param {string} color - The color of the typography: primary, secondary, tertiary, success, warning, info, placeholder, inverse, error, link, visitedLink, hoverPrimary, disabled, disabled02
 * @returns {string} The class names for the typography styles.
 * @usage  basic examples:
 * @example
 * <h1 className={typographyVariants({ variant: 'h1', color: 'primary' })}>
 *   This is a primary color h1
 * </h1>
 * <p className={typographyVariants({ variant: 'bodyShort01', color: 'secondary' })}>
 *   This is a secondary color bodyShort01
 * </p>
 *
 * @usage If you want to use the typography with a custom styles, you can use the cn function from `mint-lib/tw-utils`.
 * @example
 * <p className={cn('text-red-500', typographyVariants({ variant: 'bodyShort01' }))}>
 *   This is a red color bodyShort01
 * </p>
 */

const typographyVariants = cva('tracking-normal', {
  variants: {
    variant: {
      h1: ['text-[32px]', 'leading-10', 'font-medium'],
      h2: ['text-[25px]', 'leading-8', 'font-semibold'],
      h3: ['text-[21px]', 'leading-6', 'font-semibold'],
      h4: ['text-[17px]', 'leading-5', 'font-semibold'],
      h5: ['text-[15px]', 'leading-5', 'font-medium'],
      h6: ['text-[14px]', 'leading-4', 'font-semibold'],
      bodyShort01: ['text-[14px]', 'leading-4', 'font-normal'],
      bodyShort02: ['text-[14px]', 'leading-4', 'font-medium'],
      bodyLong01: ['text-[14px]', 'leading-5', 'font-normal'],
      bodyLong02: ['text-[14px]', 'leading-5', 'font-medium'],
      bodyMobile01: ['text-[18px]', 'leading-5', 'font-normal'],
      bodyMobile02: ['text-[18px]', 'leading-5.5', 'font-medium'],
      bodyMobile03: ['text-[18px]', 'leading-6', 'font-semibold'],
      caption1: ['text-[12px]', 'leading-4', 'font-medium'],
      caption2: ['text-[11px]', 'leading-3', 'font-normal'],
      caption3: ['text-[8px]', 'leading-3', 'font-normal'],
      label01: ['text-[12px]', 'leading-4', 'font-normal'],
      overline1: ['text-[11px]', 'leading-3', 'font-normal', 'uppercase'],
      helperText01: ['text-[12px]', 'leading-4', 'font-normal'],
    },
    color: {
      primary: 'text-text-primary',
      secondary: 'text-text-secondary',
      tertiary: 'text-text-tertiary',
      success: 'text-text-success',
      warning: 'text-text-warning',
      info: 'text-text-info',
      placeholder: 'text-text-placeholder',
      inverse: 'text-text-inverse',
      error: 'text-text-error',
      link: 'text-text-link',
      visitedLink: 'text-text-visitedLink',
      hoverPrimary: 'text-text-hoverPrimary',
      disabled: 'text-text-disabled',
      disabled02: 'text-text-disabled02',
    },
    link: {
      true: 'cursor-pointer text-text-link visited:text-text-visitedLink hover:underline focus:outline-2 focus:outline-offset-1 focus:outline-action-focusBorder aria-disabled:text-text-disabled aria-disabled:hover:no-underline',
      false: null,
    },
  },
});
export { typographyVariants };
